'use client'

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { cva, type VariantProps } from 'class-variance-authority'
import { Circle } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { Label } from './label'

const radioGroupVariants = cva('grid gap-2', {
  variants: {
    disabled: {
      true: 'opacity-50 cursor-not-allowed',
      false: '',
    },
  },
  defaultVariants: {
    disabled: false,
  },
})

const radioItemVariants = cva(
  'aspect-square rounded-full border ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'border-neutral-500 text-primary data-[state=checked]:enabled:border-primary data-[state=checked]:enabled:text-primary',
        secondary:
          'border-secondary text-secondary data-[state=checked]:enabled:border-secondary data-[state=checked]:enabled:text-secondary',
        neutral:
          'border-neutral-900 text-neutral-900 data-[state=checked]:enabled:border-neutral-900 data-[state=checked]:enabled:text-neutral-900',
      },
      size: {
        default: 'h-5 w-5',
        small: 'h-4 w-4',
      },
      disabled: {
        true: 'border-neutral-600/30 cursor-not-allowed text-neutral-600/30',
        false: '',
      },
    },
    defaultVariants: {
      size: 'default',
      disabled: false,
      variant: 'primary',
    },
  }
)

const indicatorVariants = cva('fill-current text-current', {
  variants: {
    size: {
      default: 'h-2.5 w-2.5',
      small: 'h-2 w-2',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

interface RadioGroupProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
      'disabled'
    >,
    VariantProps<typeof radioGroupVariants> {}

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, disabled, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn(radioGroupVariants({ disabled, className }))}
      {...props}
      ref={ref}
    />
  )
})
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

interface RadioGroupItemProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
      'disabled'
    >,
    VariantProps<typeof radioItemVariants> {
  children?: React.ReactNode
}
const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps & { wrapperClassName?: string; labelClassName?: string }
>(
  (
    {
      className,
      children,
      size,
      disabled,
      variant,
      wrapperClassName,
      labelClassName,
      ...props
    },
    ref
  ) => {
    return (
      <div className={cn('flex items-center space-x-2', wrapperClassName)}>
        <RadioGroupPrimitive.Item
          ref={ref}
          id={`radio-${props.value}`}
          className={cn(
            radioItemVariants({ size, disabled, variant, className })
          )}
          disabled={!!disabled}
          {...props}
        >
          <RadioGroupPrimitive.Indicator className='flex items-center justify-center'>
            <Circle className={cn(indicatorVariants({ size }))} />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
        {children && (
          <Label
            htmlFor={`radio-${props.value}`}
            className={cn(
              'cursor-pointer',
              disabled && 'cursor-not-allowed',
              labelClassName
            )}
            size={
              size === 'default'
                ? undefined
                : size === 'small'
                  ? 'small'
                  : undefined
            }
          >
            {children}
          </Label>
        )}
      </div>
    )
  }
)
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

export { RadioGroup, RadioGroupItem }
